import { GlobalConfigType } from 'actions'
import { FieldOrigin, IVisibleProp, LoanFieldProps } from 'config'
import { convertValueToLabel, getLoanFieldOption } from 'config/loan.constants'
import { getLoanStructureCalculatedValues } from 'pages/LoanStructure/Logic'
import { store } from 'reducers'

import { getAdditionalPropertyFieldProps } from './loan'

const getFieldPropertiesForLinkedSource = (
  linkedField: NonNullable<IVisibleProp['linkedField']>,
  loanFieldProperties: LoanFieldProps,
): Record<string, IVisibleProp> => {
  if (linkedField.fieldOrigin === FieldOrigin.LoanStructure) {
    if (linkedField.fieldKey === 'additionalProperties') {
      return {
        ...((loanFieldProperties[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>),
        ...getAdditionalPropertyFieldProps(loanFieldProperties),
      }
    }
    return (loanFieldProperties[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>
  }

  return (loanFieldProperties[linkedField.fieldOrigin as FieldOrigin] || {}) as Record<string, IVisibleProp>
}

const convertLinkedFieldDisplayValue = (
  sourceFieldKey: string,
  value: any,
  fieldProperties: Record<string, IVisibleProp>,
): any => {
  if (value === null || value === undefined || value === '') return value

  let label = convertValueToLabel(sourceFieldKey, value, fieldProperties)
  if (label !== String(value)) return label

  const optionMap = getLoanFieldOption(sourceFieldKey, fieldProperties)
  if (optionMap[value] !== undefined) return optionMap[value]

  const fieldProp = fieldProperties[sourceFieldKey]
  if (!fieldProp?.options) return label

  if (Array.isArray(fieldProp.options)) {
    const option = (fieldProp.options as any[]).find((item) => item?.key == value)
    if (option?.title) return option.title
  } else if (typeof fieldProp.options === 'object' && (fieldProp.options as Record<string, any>)[value] !== undefined) {
    return (fieldProp.options as Record<string, any>)[value]
  }

  return label
}

const hasSetValue = (value: any) => value !== null && value !== undefined && value !== ''

/**
 * "Additional Property 2" uses option0 = 0 and maps to property #2 overall.
 * Property #1 (subject) lives on the loan root; property #2+ live in multipleProperties.
 * When multiple-properties mode is off, property data is stored on the loan root instead.
 */
const resolveAdditionalPropertyFieldValue = (
  loan: Record<string, any>,
  option0: string,
  option1: string,
): any => {
  const additionalIndex = Number(option0)
  if (isNaN(additionalIndex) || !option1) return undefined

  const propertyNumber = additionalIndex + 2
  const multiplePropertiesIndex = propertyNumber - 2

  const propertyValue = loan.multipleProperties?.[multiplePropertiesIndex]?.[option1]
  if (hasSetValue(propertyValue)) return propertyValue

  // Single-property loans store all property fields on the loan root object.
  if (!loan.isMultipleProperties && propertyNumber === 2) return loan[option1]

  return propertyValue
}

/**
 * Resolves the display value for a field that has a `linkedField` configuration,
 * pointing to a source field on another page.
 *
 * For LoanStructure calculated fields (ltv, cltv, ltc, …) the live computed
 * value is returned.  For regular loan-state fields the value is read directly
 * from the loan object by the linked key.
 *
 * Select and other option-backed fields are converted to their display labels.
 *
 * Returns `{ resolved: true, value }` when a linked field config is present and
 * fully specified, or `{ resolved: false, value: undefined }` otherwise – letting
 * callers fall back to their own default value logic.
 */
export const resolveLinkedFieldValue = (
  fieldProps: IVisibleProp | undefined,
  loan: Record<string, any>,
): { resolved: boolean; value: any } => {
  const linkedField = fieldProps?.linkedField
  if (!linkedField?.fieldOrigin || !linkedField.fieldKey) return { resolved: false, value: undefined }

  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldPropertiesForLabel = getFieldPropertiesForLinkedSource(linkedField, loanFieldProperties)

  let rawValue: any
  let sourceFieldKey: string

  if (linkedField.fieldOrigin === FieldOrigin.LoanStructure) {
    const calculatedValues = getLoanStructureCalculatedValues(loan as any)
    if (Object.prototype.hasOwnProperty.call(calculatedValues, linkedField.fieldKey)) {
      rawValue = calculatedValues[linkedField.fieldKey]
      sourceFieldKey = linkedField.fieldKey
    } else if (linkedField.fieldKey === 'additionalProperties' && linkedField.option0 !== undefined) {
      sourceFieldKey = linkedField.option1 || ''
      rawValue = resolveAdditionalPropertyFieldValue(loan, linkedField.option0, sourceFieldKey)
    } else {
      rawValue = loan[linkedField.fieldKey]
      sourceFieldKey = linkedField.fieldKey
    }
  } else {
    rawValue = loan[linkedField.fieldKey]
    sourceFieldKey = linkedField.fieldKey
  }

  return {
    resolved: true,
    value: convertLinkedFieldDisplayValue(sourceFieldKey, rawValue, fieldPropertiesForLabel),
  }
}
